import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import {
    currencyFormate,
    formatInputToCapitalization,
    getVoucherDate,
    removeCurrencyComma,
} from "../Date_Formate_Utilitiy/Index";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { openModal, closeModal } from "../../redux/actions/action-creator";

const FIELD_ORDER = ["credit_debit", "first", "debit_amount", "credit_amount"];

export const useJournalVoucher = ({
    data,
    setData,
    validations,
    inputRefs,
    dynamicInputRef,
    setNameInput,
    contra_account_ledger_list,
    isView
}) => {
    const dispatch = useDispatch();
    const [submitConfirm, setSubmitConfirm] = useState("");
    const { company_session } = useSelector((state) => state.common);
    const modal_state = useSelector((state) => state.common.modal_state);

    const standardModals = [
        'ledgerCreate', 'godownCreateVoucher', 'stockItemCreate',
        'period', 'changeMode', 'configure', 'voucherTypeNameSelect',
        'dispatchDetails', 'orderDetails', 'otherVoucher', 'create_group_under',
        'create_under', 'create_stock_cat', 'create_stock_company', 'create_unit'
    ];

    const isDOMModalOpen = !!(document.querySelector('.modal_link') || document.querySelector('.modal_message'));
    const isAnyModalOpen = Object.keys(modal_state || {}).some(key => modal_state[key]) || isDOMModalOpen;
    const modalStateRef = useRef(modal_state);
    const idCounterRef = useRef(0);

    useEffect(() => {
        modalStateRef.current = modal_state;
    }, [modal_state]);

    useEffect(() => {
        if (data?.data?.length > 0) {
            const maxId = Math.max(...data.data.map(i => parseInt(i.id) || 0));
            if (maxId > idCounterRef.current) {
                idCounterRef.current = maxId;
            }
        }
    }, [data?.data]);

    const cleanString = (str) => str?.replace(/[^a-zA-Z0-9]/g, "")?.toLowerCase();

    const safeParseNumber = (value) => {
        if (value == null || value === '' || value === undefined) return 0;
        if (typeof value === 'number') return isNaN(value) ? 0 : value;
        const cleaned = removeCurrencyComma(String(value));
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Calculate totals and balance
    useEffect(() => {
        if (!data?.data) return;
        let totalDebit = 0;
        let totalCredit = 0;

        data.data.forEach(row => {
            const isDr = row.credit_debit?.toLowerCase() === 'dr' || (!row.credit_debit && data.data.indexOf(row) === 0);
            if (isDr) {
                totalDebit += safeParseNumber(row.debit_amount);
            } else {
                totalCredit += safeParseNumber(row.credit_amount);
            }
        });

        if (totalDebit !== data.total_debit || totalCredit !== data.total_credit) {
            setData(prev => ({
                ...prev,
                total_debit: totalDebit,
                total_credit: totalCredit,
                total_amount: totalDebit // For compatibility with existing components
            }));
        }
    }, [data.data]);

    const handleItemUpdate = useCallback((e, id, field, value) => {
        // console.log("handleItemUpdate called:", { field, id, value });
        if (submitConfirm || isView) {
            if (e && e.preventDefault && e.key !== undefined) e.preventDefault(); // Only block keyboard events
            return;
        }

        switch (field) {
            case "credit_debit":
                const val = value?.toLowerCase();
                if (val === 'd' || val === 'b') value = 'Dr';
                else if (val === 'c' || val === 't') value = 'Cr';

                if (value === 'Dr' || value === 'Cr' || value === '') {
                    setData(prev => ({
                        ...prev,
                        data: prev.data.map(item => item.id === id ? {
                            ...item,
                            [field]: value,
                            // Clear opposite amount if type changes
                            debit_amount: value === 'Cr' ? '' : item.debit_amount,
                            credit_amount: value === 'Dr' ? '' : item.credit_amount
                        } : item)
                    }));
                }
                break;

            case "first":
                const listToVerify = [{ ledger_name: "End of List" }, ...(contra_account_ledger_list || [])];
                const exactMatch = listToVerify?.find(item => cleanString(item?.ledger_name) === cleanString(value));
                setData(prev => ({
                    ...prev,
                    data: prev.data.map((item, index) => String(item.id) === String(id) ? {
                        ...item,
                        [field]: value,
                        // Set ID and balance if exact match found, otherwise keep existing if we're just typing
                        particular_ledger_id: exactMatch ? (exactMatch.id || exactMatch.ledger_id || "") : (value === "" ? "" : item.particular_ledger_id),
                        particular_ledger_balance: exactMatch ? (exactMatch.opening_balance || "") : (value === "" ? "" : item.particular_ledger_balance),
                        // Ensure credit_debit is set for the first row if empty
                        credit_debit: item.credit_debit || (index === 0 ? 'Dr' : '')
                    } : item)
                }));
                break;

            case "debit_amount":
            case "credit_amount":
                const cleanVal = value.replace(/,/g, "");
                if (/^[0-9.\b]*$/.test(cleanVal) || cleanVal === "") {
                    setData(prev => ({
                        ...prev,
                        data: prev.data.map(item => {
                            if (item.id === id) {
                                const updatedItem = { ...item, [field]: cleanVal };
                                // Sync for backend compatibility
                                updatedItem.particular_ledger_pay_amount = cleanVal;
                                return updatedItem;
                            }
                            return item;
                        })
                    }));
                }
                break;
        }
        if (e && e.key === "Enter") {
            e.preventDefault();
        }
    }, [isView, submitConfirm, contra_account_ledger_list, setData]);

    const addRow = useCallback(() => {
        idCounterRef.current += 1;
        const next_id = String(idCounterRef.current);
        setData(prev => ({
            ...prev,
            data: [...(prev.data || []), {
                id: next_id,
                credit_debit: (prev.total_debit > prev.total_credit) ? 'Cr' : 'Dr',
                first: "",
                particular_ledger_balance: "",
                particular_ledger_id: "",
                debit_amount: "",
                credit_amount: "",
                prev_value: ""
            }]
        }));
        return next_id;
    }, [setData]);

    const handleDropdownClick = useCallback((name, value) => {
        const fields = Object.keys(inputRefs).filter(f => inputRefs[f]?.current && !inputRefs[f].current.disabled);
        const currentIndex = fields.indexOf(name);
        if (currentIndex === -1) return;
        const nextField = fields[currentIndex + 1] || null;

        if (name === "create_date") {
            const firstRowId = data?.data?.[0]?.id;
            const nextRef = dynamicInputRef.current[firstRowId]?.credit_debit || dynamicInputRef.current[firstRowId]?.first;
            if (nextRef) {
                nextRef.focus();
                nextRef.setSelectionRange(0, nextRef.value?.length || 0);
            }
        } else if (nextField && inputRefs[nextField]?.current) {
            inputRefs[nextField].current.focus();
            setSubmitConfirm("");
        } else if (name === "narration") {
            setSubmitConfirm("Accept");
        }
    }, [data.data, dynamicInputRef, inputRefs, setSubmitConfirm]);

    const handleFallbackNavigation = useCallback((e, id, field, value) => {
        const rowRef = dynamicInputRef.current[id];
        if (!rowRef) return;

        const row = data.data.find(r => String(r.id) === String(id));
        const isDr = row?.credit_debit?.toLowerCase() === 'dr' || (!row?.credit_debit && data.data.indexOf(row) === 0);

        const effectiveOrder = isDr ? ["credit_debit", "first", "debit_amount"] : ["credit_debit", "first", "credit_amount"];
        const currentFieldIndex = effectiveOrder.indexOf(field);

        if (currentFieldIndex !== -1 && currentFieldIndex < effectiveOrder.length - 1) {
            const nextField = effectiveOrder[currentFieldIndex + 1];

            if (field === "first" && (value === "" || value.toLowerCase() === "end of list")) {
                const isBalanced = data?.total_debit > 0 && Math.abs((data?.total_debit || 0) - (data?.total_credit || 0)) < 0.01;
                if (isBalanced) {
                    setData(prev => ({
                        ...prev,
                        data: prev.data.filter(item => item.id !== id)
                    }));
                    inputRefs.narration.current.focus();
                    return;
                } else {
                    if (value !== "") {
                        toast.error("Voucher is not balanced");
                    }
                    return;
                }
            }

            const nextRef = rowRef[nextField];
            if (nextRef) {
                if (nextField === "debit_amount" || nextField === "credit_amount") {
                    if (!safeParseNumber(nextRef.value)) {
                        const diff = Math.abs((data.total_debit || 0) - (data.total_credit || 0));
                        if (diff > 0) {
                            handleItemUpdate({ preventDefault: () => { } }, id, nextField, diff.toFixed(2));
                        }
                    }
                }
                setNameInput(nextRef);
                nextRef.focus();
                nextRef.setSelectionRange(0, nextRef.value?.length || 0);
                return;
            }
        }

        const allRowIds = data.data.map(r => r.id);
        const currentRowIndex = allRowIds.indexOf(id);
        const nextRowId = allRowIds[currentRowIndex + 1];

        if (nextRowId) {
            const nextFieldRef = dynamicInputRef.current[nextRowId]?.["first"];
            if (nextFieldRef) {
                setNameInput(nextFieldRef);
                nextFieldRef.focus();
                return;
            }
        }

        // Handle Narration jump or New Row logic
        if (row?.first && row.first !== "End of List") {
            const newId = addRow();
            setTimeout(() => {
                const newRef = dynamicInputRef.current[newId]?.["first"];
                if (newRef) {
                    setNameInput(newRef);
                    newRef.focus();
                }
            }, 50);
        } else {
            inputRefs.narration.current.focus();
        }
    }, [data.data, data.total_debit, data.total_credit, dynamicInputRef, inputRefs, setData, setNameInput, handleItemUpdate, addRow]);

    const handleDynamicFocus = useCallback((e, id, field, value) => {
        if (window.isRestoringVoucherFocus) {
            return;
        }
        if (submitConfirm || isView) {
            e.preventDefault();
            e.target.blur();
            return;
        }

        if ((field === "debit_amount" || field === "credit_amount") && value) {
            const cleanVal = value.replace(/,/g, "");
            if (cleanVal !== value) {
                setData(prev => ({
                    ...prev,
                    data: prev.data.map(item => item.id === id ? { ...item, [field]: cleanVal } : item)
                }));
            }
        }

        const ref = dynamicInputRef.current[id]?.[field];
        if (ref) {
            setNameInput(ref);
            ref.setSelectionRange(0, ref.value?.length || 0);
        }
        if (field === "first") {
            dispatch(openModal(`${field}${id}`));
        }
    }, [isView, submitConfirm, setData, setNameInput, dispatch, dynamicInputRef]);

    const handleDynamicBlur = useCallback((e, id, field, value) => {
        const refKey = `${field}${id}`;
        if (field === "first" && modalStateRef.current?.[refKey]) {
            dispatch(closeModal(refKey));
        }
        if (field === "debit_amount" || field === "credit_amount") {
            const num = safeParseNumber(value);
            if (num > 0) {
                setData(prev => ({
                    ...prev,
                    data: prev.data.map(item => {
                        if (item.id === id) {
                            const formatted = currencyFormate(num);
                            return {
                                ...item,
                                [field]: formatted,
                                particular_ledger_pay_amount: formatted
                            };
                        }
                        return item;
                    })
                }));
            }
        }
    }, [dispatch, setData]);

    const handleKeyDown = useCallback((e, id, field) => {
        if (isView) {
            e.preventDefault();
            return;
        }

        const isNavigationKey = ["Enter", "Tab", "ArrowDown", "ArrowUp", "Escape"].includes(e.key);

        if (submitConfirm && isNavigationKey) {
            e.preventDefault();
            return;
        }

        if (isAnyModalOpen && (e.key === "Enter" || e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Tab")) return;

        const allRowIds = data.data.map(r => r.id);

        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            const value = e.target.value;
            if (field) {
                handleFallbackNavigation(e, id, field, value);
            } else {
                handleDropdownClick(e.target.name, value);
            }
        } else if (e.key === "Tab") {
            e.preventDefault();
            e.stopPropagation();
            const value = e.target.value;

            if (e.shiftKey) {
                // Backward navigation
                if (id && field) {
                    const row = data.data.find(r => r.id === id);
                    const isDr = row?.credit_debit?.toLowerCase() === 'dr' || (!row?.credit_debit && data.data.indexOf(row) === 0);
                    const effectiveOrder = isDr ? ["credit_debit", "first", "debit_amount"] : ["credit_debit", "first", "credit_amount"];
                    const currentFieldIndex = effectiveOrder.indexOf(field);

                    if (currentFieldIndex > 0) {
                        const prevField = effectiveOrder[currentFieldIndex - 1];
                        const prevRef = dynamicInputRef.current[id]?.[prevField];
                        if (prevRef) {
                            prevRef.focus();
                            prevRef.setSelectionRange(0, prevRef.value?.length || 0);
                        }
                    } else {
                        const currentRowIndex = allRowIds.indexOf(id);
                        if (currentRowIndex > 0) {
                            const prevRowId = allRowIds[currentRowIndex - 1];
                            const prevRow = data.data.find(r => r.id === prevRowId);
                            const prevIsDr = prevRow?.credit_debit?.toLowerCase() === 'dr' || (!prevRow?.credit_debit && data.data.indexOf(prevRow) === 0);
                            const prevField = prevIsDr ? "debit_amount" : "credit_amount";
                            const prevRef = dynamicInputRef.current[prevRowId]?.[prevField];
                            if (prevRef) {
                                prevRef.focus();
                                prevRef.setSelectionRange(0, prevRef.value?.length || 0);
                            }
                        } else {
                            inputRefs.create_date.current.focus();
                        }
                    }
                } else if (e.target.name === "narration") {
                    const lastRowId = allRowIds[allRowIds.length - 1];
                    const lastRow = data.data.find(r => r.id === lastRowId);
                    const lastIsDr = lastRow?.credit_debit?.toLowerCase() === 'dr' || (!lastRow?.credit_debit && data.data.indexOf(lastRow) === 0);
                    const lastField = lastIsDr ? "debit_amount" : "credit_amount";
                    const lastRef = dynamicInputRef.current[lastRowId]?.[lastField];
                    if (lastRef) {
                        lastRef.focus();
                        lastRef.setSelectionRange(0, lastRef.value?.length || 0);
                    }
                } else {
                    const fields = Object.keys(inputRefs).filter(f => inputRefs[f]?.current && !inputRefs[f].current.disabled);
                    const currentIndex = fields.indexOf(e.target.name);
                    if (currentIndex > 0) {
                        const prevField = fields[currentIndex - 1];
                        inputRefs[prevField].current.focus();
                    }
                }
            } else {
                // Forward navigation
                if (field) {
                    handleFallbackNavigation(e, id, field, value);
                } else {
                    handleDropdownClick(e.target.name, value);
                }
            }
        }
    }, [isView, submitConfirm, isAnyModalOpen, data.data, handleFallbackNavigation, handleDropdownClick, dynamicInputRef, inputRefs]);



    const handleDropdownDynamicClick = useCallback((field, value, id) => {
        const ledger = (contra_account_ledger_list || [])?.find(l => l.ledger_name === value);

        setData(prev => ({
            ...prev,
            data: prev.data.map((item, i) => String(item.id) === String(id) ? {
                ...item,
                [field]: value,
                particular_ledger_id: ledger ? (ledger.id || ledger.ledger_id || "") : item.particular_ledger_id,
                particular_ledger_balance: ledger ? (ledger.opening_balance || "") : item.particular_ledger_balance,
                // Ensure credit_debit is set for the first row if empty
                credit_debit: item.credit_debit || (i === 0 ? 'Dr' : '')
            } : item)
        }));

        dispatch(closeModal(`${field}${id}`));

        // Use setTimeout to ensure the modal closing doesn't interfere with focus movement
        setTimeout(() => {
            handleFallbackNavigation(null, id, field, value);
        }, 50);
    }, [contra_account_ledger_list, setData, dispatch, handleFallbackNavigation]);

    return {
        handleChange: (e) => setData(prev => ({ ...prev, [e.target.name]: e.target.value })),
        handleFocus: (e) => {
            if (window.isRestoringVoucherFocus) {
                return;
            }
            const ref = inputRefs[e.target.name];
            if (ref) {
                setNameInput(ref);
                ref.current.setSelectionRange(0, ref.current.value.length);
            }
        },
        handleBlur: (e) => {
            const { name, value } = e.target;
            if (name === 'create_date' && value) {
                const dateResult = getVoucherDate(value, company_session, data?.create_date);
                if (dateResult?.formattedDateString) {
                    setData(prev => ({ ...prev, create_date: dateResult.formattedDateString, day_name: dateResult.day_show }));
                }
            }
        },
        submitConfirm,
        setSubmitConfirm,
        handleItemUpdate,
        handleDynamicFocus,
        handleDynamicBlur,
        handleDropdownClick,
        handleDropdownDynamicClick,
        handleKeyDown,
    };
};
